"use client";

import { ChangeEvent, useState, memo, InputHTMLAttributes, useEffect, useMemo, Fragment } from "react";
import { Popover, Transition } from "@headlessui/react";
import { captureException } from "@sentry/nextjs";
import { usePathname } from "next/navigation";
import CloseIcon from "components/icons-svg/CloseIcon";
import GrMapCountryIcon from "components/icons-svg/GrMapCountryIcon";
import mdlocationcityIcon from "components/icons-svg/mdlocationcityIcon";
import NewGrLocationIcon from "components/icons-svg/NewGrLocationIcon";
import NewLocation from "components/icons-svg/NewLocation";
import NewmdlocationcityIcon from "components/icons-svg/NewmdlocationcityIcon";
import SearchPlaceSuggestions from "components/searchBar/SearchPlaceSuggestions";
import SuggestionListWithFallback from "components/searchBar/SuggestionListWithFallback";
import { useDebounce } from "hooks/use-debounce";
import { Status, getGeoCodesByPlaceId } from "services/api/googleAPIs";
import { selectUser } from "stores/auth/authSlice";
import { useAppDispatch, useAppSelector } from "stores/hooks";
import { selectLocationName, setSearchAddressResult } from "stores/save-search/saveSearchSlice";
import { markSearch, selectSearch } from "stores/search/searchSlice";
import { CurrentSearchTabType } from "stores/ui/types";
import { markSearchFlow } from "stores/ui/uiSlice";
import { isDesktop, waitForGoogleMaps } from "utils/dom";
import { processPlaceDescription, stringifyBounds } from "utils/helper";
import { useDisclosure } from "utils/hooks/useDisclosure";
import { useMediaQuery } from "utils/hooks/useMediaQuery";
export interface NewDataType {
  description: string;
  place_id: string;
  types: string[];
}
export const iconMap = {
  Region: NewGrLocationIcon,
  City: mdlocationcityIcon,
  Country: GrMapCountryIcon,
  Establishment: NewmdlocationcityIcon
};
export function autoCompleteAPI(value: string, displaySuggestions: (predictions: google.maps.places.AutocompletePrediction[] | null, status: google.maps.places.PlacesServiceStatus) => void) {
  const autoComplete = new google.maps.places.AutocompleteService();
  const len = value.trim().length;
  const componentRestrictions = len <= 2 ? {
    country: ["us"]
  } : len <= 4 ? {
    country: ["us", "ca", "mx", "gb", "fr"]
  } : undefined;
  void autoComplete.getPlacePredictions({
    input: value,
    language: "en",
    // region: "us",
    location: new google.maps.LatLng(39.8283, -98.5795),
    radius: 3200000,
    componentRestrictions
  }, displaySuggestions);
}
const SearchBarInputWithoutMemo = ({
  handleDateFocus,
  boxShadow = true,
  haveIcon = false,
  label,
  closeIconClass = " right-2.5",
  popoverStyle = "",
  inputStyle = "",
  placeholderText = "Ex: “Seattle” or “Texas” or “Italy”",
  ...rest
}: {
  handleDateFocus?: () => void;
  boxShadow?: boolean;
  haveIcon?: boolean;
  label?: string;
  closeIconClass?: string;
  popoverStyle?: string;
  inputStyle?: string;
  placeholderText?: string;
} & InputHTMLAttributes<HTMLInputElement>) => {
  const dispatch = useAppDispatch();
  const search = useAppSelector(selectSearch);
  const user = useAppSelector(selectUser);
  const pathname = usePathname();
  const isHomePage = pathname === "/";
  const isVacationRentalPage = pathname?.startsWith("/vacation-rentals");
  const isDetailPage = pathname?.startsWith("/detail");
  const [isLargerThan456] = useMediaQuery("(min-width: 456px)");
  const {
    isOpen,
    onClose,
    onOpen
  } = useDisclosure();
  const [init, setInit] = useState(false);
  const [address, setAddress] = useState("");
  const [addressData, setAddressData] = useState("");
  const [inputText, setInputText] = useState<string>(search?.shortName || "");
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [hasLoadedSuggestions, setHasLoadedSuggestions] = useState(false);
  const debouncedInputText = useDebounce(inputText || "", 100);
  const isRegionalPage = pathname?.startsWith("/locations");
  const locationName = useAppSelector(selectLocationName);
  useEffect(() => {
    if (isRegionalPage) {
      setAddressData(locationName);
      dispatch(markSearch({
        shortName: locationName,
        longName: locationName
      }));
    }
  }, [dispatch, isRegionalPage, locationName]);
  const handleSearchFlow = (currentSearchTab: CurrentSearchTabType) => {
    dispatch(markSearchFlow({
      currentSearchTab
    }));
  };
  const onFocus = () => {
    if (isOpen) return;
    onOpen();
    handleSearchFlow("location");
    void (async () => {
      const mixpanelV2Client = (await import("services/mixpanelv2")).mixpanelV2Client;
      mixpanelV2Client.CLICK_OPEN_SEARCH({
        pageOrigin: isVacationRentalPage ? "search results page" : "property detail page",
        actionType: isVacationRentalPage ? "modify" : "initial"
      });
    })();
  };
  const [addressList, setAddressList] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const inputValue = useMemo(() => {
    if (init) {
      return address || inputText;
    }
    if (search.isSearchingInMap && !search.isBoundaryVisible) {
      return "Map area";
    }

    // Get the raw display name from search or addressData
    const rawName = search?.longName || addressData || "";

    // ✅ Clean up location name
    const cleanedName = (() => {
      // Split by comma to isolate parts
      const parts = rawName.split(",").map(p => p.trim());
      if (parts.length === 0) return rawName;

      // If it’s a known "City, State, Country" → return just the city
      if (parts.length === 3) return parts[0];

      // If it’s "State, Country" (e.g., "Florida, USA") → return just state
      if (parts.length === 2 && parts[1].toLowerCase().includes("usa")) return parts[0];

      // For regions or destinations like "Yosemite National Park, California, USA"
      // → return the first part
      return parts[0];
    })();
    return cleanedName;
  }, [address, addressData, init, inputText, search.isSearchingInMap, search?.longName, search.isBoundaryVisible]);
  const whatShouldBeOpenNext = () => {
    if ((isVacationRentalPage || isDetailPage) && !isDesktop()) {
      return;
    }
    if (!search.startDate && !search.endDate) {
      handleDateFocus?.();
      if (isLargerThan456) {
        // as in mobile view date picker should NOT auto-open
        dispatch(markSearchFlow({
          currentSearchTab: "dates"
        }));
      }
      return;
    }
    if (isHomePage && isDesktop()) {
      dispatch(markSearchFlow({
        currentSearchTab: null
      }));
      return;
    }
    const adCount = search?.adultCount ? search.adultCount : 0;
    const childCount = search?.childrenCount ? search.childrenCount : 0;
    if (adCount <= 0 && childCount <= 0) {
      dispatch(markSearchFlow({
        currentSearchTab: "guest"
      }));
      return;
    }
    dispatch(markSearchFlow({
      currentSearchTab: null
    }));
    return;
  };
  const onPlaceSelected = (place: NewDataType) => {
    if (window?.googleMapsLoaded === false) {
      return;
    }
    const getGeocodeFromPlaceId = async (placeId: string) => {
      const data = await getGeoCodesByPlaceId(placeId);
      return data?.data;
    };
    getGeocodeFromPlaceId(place.place_id).then(res => {
      if (res == null || res?.status !== Status.OK) {
        return;
      }
      const selectedPlace = res?.results[0];
      let bounds: string | null = null;
      if (selectedPlace?.geometry?.bounds || selectedPlace?.geometry?.viewport) {
        const temp = selectedPlace?.geometry?.viewport || selectedPlace?.geometry?.bounds;
        const ne = temp?.northeast;
        const sw = temp?.southwest;
        if (ne && sw) {
          bounds = stringifyBounds({
            se: {
              lat: sw.lat,
              lng: ne.lng
            },
            nw: {
              lat: ne.lat,
              lng: sw.lng
            },
            sw: sw,
            ne: ne
          });
        }
      }
      const lat: number = selectedPlace.geometry.location.lat;
      const lng: number = selectedPlace.geometry.location.lng;
      const placeId = selectedPlace?.place_id;
      const placeType = selectedPlace.types?.[0] || selectedPlace.geometry?.location_type;
      setAddressList([]);
      setAddressData(place.description);
      setAddress("");
      setInit(false);
      setHasLoadedSuggestions(false);
      dispatch(markSearch({
        lat,
        lng,
        placeId,
        placeType,
        shortName: processPlaceDescription(place.description || ""),
        longName: place.description,
        page: 0,
        bounds,
        isSearchingInMap: false,
        isBoundaryVisible: true,
        isBoundaryAutoRemoved: false,
        propertyConfigTypeId: null
      }));
      dispatch(setSearchAddressResult({
        address_components: selectedPlace?.address_components
      }));
      whatShouldBeOpenNext();
      onClose();
      return null;
    }).catch(e => {
      dispatch(markSearch({
        shortName: processPlaceDescription(place.description || ""),
        longName: place.description,
        page: 0,
        bounds: null,
        isSearchingInMap: false,
        propertyConfigTypeId: null
      }));
      whatShouldBeOpenNext();
      onClose();
      setAddress("");
      setAddressList([]);
      console.error(e);
      captureException(new Error(`Error 'getGeocode' ${JSON.stringify(e)}`));
    });

    // user identify mixpanel
    if (user && user.userId) {
      void (async () => {
        const mixpanelV1Client = (await import("services/mixpanel")).mixpanelV1Client;
        const mixpanelV2Client = (await import("services/mixpanelv2")).mixpanelV2Client;
        if (user && user.userId) {
          mixpanelV1Client.IDENTIFY(user.userId);
          mixpanelV2Client.IDENTIFY(user.userId);
        }
      })();
    }
  };
  useEffect(() => {
    if (!debouncedInputText) {
      setAddressList([]);
      setIsLoadingSuggestions(false);
      setHasLoadedSuggestions(false);
      return;
    }
    setIsLoadingSuggestions(true);
    const displaySuggestions = function (predictions: google.maps.places.AutocompletePrediction[] | null, status: google.maps.places.PlacesServiceStatus) {
      setIsLoadingSuggestions(false);
      setHasLoadedSuggestions(true);
      if (status != google.maps.places.PlacesServiceStatus.OK) {
        setAddressList([]);
        return;
      }
      const filteredSuggestions = predictions?.filter(suggestion => {
        return suggestion.types && suggestion.types.indexOf("restaurant") === -1 && suggestion.types.indexOf("food") === -1 && suggestion.types.indexOf("lodging") === -1 && suggestion.types.indexOf("university") === -1 && suggestion.types.indexOf("dentist") === -1 && suggestion.types.indexOf("doctor") === -1 && suggestion.types.indexOf("health") === -1 && suggestion.types.indexOf("hospital") === -1 && suggestion.types.indexOf("premise") === -1 && suggestion.types.indexOf("shopping_mall") === -1 && suggestion.types.indexOf("airport") === -1 && suggestion.types.indexOf("church") === -1 && suggestion.types.indexOf("store") === -1;
      }) || [];
      setAddressList(filteredSuggestions);
    };
    waitForGoogleMaps(() => {
      autoCompleteAPI(debouncedInputText, displaySuggestions);
    });
  }, [debouncedInputText]);
  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    if (window?.googleMapsLoaded === false) {
      return;
    }
    setAddress(e.target.value || "");
    setInputText(e.target.value || "");
    setInit(true);
  };
  return <Popover as='div' className='relative' data-sentry-element="Popover" data-sentry-component="SearchBarInputWithoutMemo" data-sentry-source-file="SearchBarInput.tsx">
      <div className={`relative ${label ? "p-1.5" : ""}`}>
        {label && <p className='px-3 text-[10px] font-medium leading-3 text-[#6B6B6B]'>
            {label}
          </p>}
        {haveIcon && <div className='absolute left-4 flex h-12 items-center text-secondary1000'>
            <NewLocation className='mr-2' />
          </div>}
        <input aria-label='search options' onFocus={() => {
        // Note: Below line was not letting the modal to close when drawer was open (820 x 1180).
        // handleSearchFlow("location")
        onFocus();
      }} onBlur={() => {
        // Add small delay to allow click events on suggestions to fire first
        setTimeout(() => {
          onClose();
        }, 200);
      }} autoComplete='off' name='addressinput' type='search' value={inputValue} onChange={handleInput} onClick={() => {
        handleSearchFlow("location");
      }} className={`google-autocomplete-places input h-12 w-full min-w-[125px] text-ellipsis rounded-lg bg-white py-3 pr-4 text-base placeholder:text-base focus-visible:shadow-none focus-visible:outline-0 lg4:pr-6 ${haveIcon ? " pl-10" : " pl-3"} ${label ? "h-[26px] border-0 !py-0 text-sm font-medium focus-visible:shadow-none" : ""} ${inputStyle}`} placeholder={placeholderText} {...rest} />
        {inputValue && <div className={`absolute top-0 flex h-12 items-center text-secondary1000 ${closeIconClass} ${label ? "top-5 h-[26px]" : ""}`}>
            <button className='btn h-auto min-w-0 p-1' aria-label='clear search location' onClick={() => {
          dispatch(markSearch({
            lat: null,
            lng: null,
            placeId: null,
            placeType: null,
            shortName: null,
            longName: null,
            bounds: null,
            isSearchingInMap: false
          }));
          setInputText("");
          setAddress("");
          setAddressData("");
          setHasLoadedSuggestions(false);
        }}>
              <CloseIcon className='h-4 w-4 lg4:h-5 lg4:w-5' />
            </button>
          </div>}
      </div>
      <Transition as={Fragment} show={isOpen} enter='transition ease-out duration-200' enterFrom='opacity-0 translate-y-1' enterTo='opacity-100 translate-y-0' leave='transition ease-in duration-150' leaveFrom='opacity-100 translate-y-0' leaveTo='opacity-0 translate-y-1' data-sentry-element="Transition" data-sentry-source-file="SearchBarInput.tsx">
        <Popover.Panel className={`${inputValue === "" ? "suggestions-pop" : ""} absolute !z-[12] mt-1 w-full min-w-[260px] rounded-2xl bg-white shadow-[0px_1px_2px_0px_#0000000d] mxd:mt-5 lg:w-max lg:max-w-[420px] xll2:max-w-[600px] ${popoverStyle}`} data-sentry-element="Popover.Panel" data-sentry-source-file="SearchBarInput.tsx">
          {inputValue === "" ? <SearchPlaceSuggestions variant='homeNew' /> : (hasLoadedSuggestions || addressList.length > 0) && <main className={`overflow-hidden rounded-2xl bg-white p-2 ${boxShadow ? "shadow" : "shadow-none"}`}>
                <SuggestionListWithFallback addressList={addressList} onSelect={onPlaceSelected} iconMap={iconMap} defaultIcon={NewmdlocationcityIcon} variant='homeNew' isLoading={isLoadingSuggestions} hasLoaded={hasLoadedSuggestions} />
              </main>}
        </Popover.Panel>
      </Transition>
    </Popover>;
};
const SearchBarInput = memo(SearchBarInputWithoutMemo);
export default SearchBarInput;